// Reusable context menu: opens at the cursor (contextmenu), from a "⋯" button, via Shift+F10 /
// the Menu key, or by long-press on touch. Arrow keys / Home / End / type-ahead navigate,
// Enter/Space activate, Esc and Tab close, click outside closes; flips to stay in the viewport.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./icons";

export type MenuEntry =
  | {
      label: string;
      onSelect: () => void;
      icon?: IconName;
      danger?: boolean;
      disabled?: boolean;
      separator?: false;
    }
  | { separator: true };

export interface MenuPosition {
  x: number;
  y: number;
}

const MARGIN = 8;

/** Keep a w×h menu opened at (x, y) inside a vw×vh viewport, flipping left/up when needed. */
export function placeMenu(x: number, y: number, w: number, h: number, vw: number, vh: number): MenuPosition {
  let left = x;
  let top = y;
  if (left + w > vw - MARGIN) left = x - w;
  if (top + h > vh - MARGIN) top = y - h;
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
  return { x: left, y: top };
}

/** Index of the next enabled entry from `from` in direction `dir` (wrapping); -1 if none. */
export function nextEnabled(enabled: boolean[], from: number, dir: 1 | -1): number {
  const n = enabled.length;
  for (let k = 1; k <= n; k++) {
    const i = (((from + dir * k) % n) + n) % n;
    if (enabled[i]) return i;
  }
  return -1;
}

export function ContextMenu({
  entries,
  position,
  onClose,
  label,
  returnFocus,
}: {
  entries: MenuEntry[];
  position: MenuPosition;
  onClose: () => void;
  label: string;
  /** Element to focus after closing via keyboard (Esc / activation). */
  returnFocus?: HTMLElement | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<MenuPosition>(position);
  const [container, setContainer] = useState<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    // Inside an open modal <dialog> (top layer) portal into it, else into <body>.
    const host = (returnFocus?.closest("dialog[open]") as Element | null) ?? document.body;
    setContainer(host);
  }, [returnFocus]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(placeMenu(position.x, position.y, r.width, r.height, window.innerWidth, window.innerHeight));
  }, [position.x, position.y, container]);

  const items = () => [...(ref.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];

  useEffect(() => {
    if (!container) return;
    const first = items().find((b) => !b.disabled);
    first?.focus();
    const close = (restore: boolean) => {
      onCloseRef.current();
      if (restore && returnFocus?.isConnected) returnFocus.focus();
    };
    const onDown = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close(false);
    };
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close(false);
    };
    const onResize = () => close(false);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("blur", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("blur", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const list = items();
    const enabled = list.map((b) => !b.disabled);
    const cur = list.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (i: number) => i >= 0 && list[i]?.focus();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focus(nextEnabled(enabled, cur < 0 ? -1 : cur, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        focus(nextEnabled(enabled, cur < 0 ? list.length : cur, -1));
        break;
      case "Home":
        e.preventDefault();
        focus(nextEnabled(enabled, -1, 1));
        break;
      case "End":
        e.preventDefault();
        focus(nextEnabled(enabled, list.length, -1));
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        onClose();
        if (returnFocus?.isConnected) returnFocus.focus();
        break;
      case "Tab":
        e.preventDefault();
        onClose();
        if (returnFocus?.isConnected) returnFocus.focus();
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
          // Type-ahead: next item starting with that letter.
          const ch = e.key.toLowerCase();
          for (let k = 1; k <= list.length; k++) {
            const i = (cur + k + list.length) % list.length;
            if (enabled[i] && (list[i].textContent ?? "").trim().toLowerCase().startsWith(ch)) {
              list[i].focus();
              break;
            }
          }
        }
    }
  };

  if (!container) return null;
  return createPortal(
    <div
      ref={ref}
      className="ctx-menu"
      role="menu"
      aria-label={label}
      style={{ left: pos.x, top: pos.y }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {entries.map((en, i) =>
        en.separator ? (
          <div key={`sep-${i}`} className="ctx-sep" role="separator" />
        ) : (
          <button
            key={en.label}
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={`menu-item${en.danger ? " danger" : ""}`}
            disabled={en.disabled}
            onClick={() => {
              onClose();
              if (returnFocus?.isConnected) returnFocus.focus();
              en.onSelect();
            }}
          >
            {en.icon ? <Icon name={en.icon} /> : <span className="icon-pad" aria-hidden="true" />}
            {en.label}
          </button>
        ),
      )}
    </div>,
    container,
  );
}

const LONG_PRESS_MS = 500;

/**
 * Wires a target element to a context menu: right-click, Shift+F10 / ContextMenu key and
 * long-press open it; `openFrom(el)` opens it below a "⋯" button.
 */
export function useContextMenu(entries: () => MenuEntry[], label: string) {
  const [state, setState] = useState<{ pos: MenuPosition; from: HTMLElement | null } | null>(null);
  const press = useRef<{ timer: number; fired: boolean } | null>(null);

  const openAt = useCallback((x: number, y: number, from: HTMLElement | null) => {
    setState({ pos: { x, y }, from });
  }, []);
  const openFrom = useCallback((el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setState({ pos: { x: r.left, y: r.bottom + 2 }, from: el });
  }, []);

  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
  };

  const targetProps = {
    onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (press.current?.fired) return;
      openAt(e.clientX, e.clientY, e.currentTarget);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if ((e.key === "F10" && e.shiftKey) || e.key === "ContextMenu") {
        e.preventDefault();
        e.stopPropagation();
        openFrom(e.currentTarget);
      }
    },
    onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      const el = e.currentTarget;
      cancelPress();
      press.current = {
        fired: false,
        timer: window.setTimeout(() => {
          if (press.current) press.current.fired = true;
          openAt(t.clientX, t.clientY, el);
        }, LONG_PRESS_MS),
      };
    },
    onTouchMove: cancelPress,
    onTouchEnd: (e: React.TouchEvent<HTMLElement>) => {
      cancelPress();
      if (press.current?.fired) e.preventDefault(); // swallow the click after a long-press
      window.setTimeout(() => (press.current = null), 0);
    },
    onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
      if (press.current?.fired) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };

  const menu: ReactNode = state ? (
    <ContextMenu
      entries={entries()}
      position={state.pos}
      label={label}
      returnFocus={state.from}
      onClose={() => setState(null)}
    />
  ) : null;

  return { targetProps, openFrom, openAt, menu, isOpen: !!state };
}

/** Small "⋯" trigger button for a context menu (also the touch/phone entry point). */
export function MenuButton({
  label,
  onOpen,
  expanded,
  className,
}: {
  label: string;
  onOpen: (el: HTMLElement) => void;
  expanded: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-btn more-btn${className ? ` ${className}` : ""}`}
      aria-label={label}
      title={label}
      aria-haspopup="menu"
      aria-expanded={expanded}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(e.currentTarget);
      }}
    >
      ⋯
    </button>
  );
}
